import {
  DEFAULT_FEATURE_FLAGS,
  DISABLED_FEATURE_FLAGS,
  FeatureFlag,
  FeatureFlags,
} from './config/featureFlags';
import { isProduction } from '../../env/EnvUtils';
import { Api } from '../../utils/api';
import { createLogger } from '../../utils/logger';

/**
 * FeatureFlagsService: Manages feature flags for the application
 * Provides a single instance of the feature flags service
 */
export class FeatureFlagsService {
  private readonly api: Api;
  private readonly isProduction: boolean;
  private readonly logger = createLogger('FeatureFlagService');

  constructor() {
    this.isProduction = isProduction();
    this.api = new Api('feature-flags-api', '/feature-flags', 'FeatureFlagService-Api');
  }

  /**
   * Get all feature flags
   * @returns FeatureFlags
   */
  async getFeatureFlags(): Promise<FeatureFlags> {
    this.logger.debug('Fetching feature flags');

    try {
      const response = await this.api.optionalAuthRequest<FeatureFlags>('GET', '');

      this.logger.debug('Feature flags response', { response });

      this.logger.debug('Feature flags fetched successfully', {
        flags: response,
      });

      return response as FeatureFlags;
    } catch (error) {
      this.logger.error('Error fetching feature flags', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      });
      return this.isProduction ? DISABLED_FEATURE_FLAGS : DEFAULT_FEATURE_FLAGS;
    }
  }

  /**
   * Get a specific feature flag
   * @param flagName - The name of the feature flag to get
   * @returns FeatureFlag | null
   */
  async getFeatureFlag(flagName: string): Promise<FeatureFlag | null> {
    this.logger.debug('Fetching feature flag', { flagName });

    const flags = await this.getFeatureFlags();
    const flag = flags[flagName];

    if (!flag) {
      this.logger.error('Feature flag not found', { flagName });
      return null;
    }

    return flag;
  }
}
